import React, { useState, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { IdentityVerifyUploadSection } from '@/components/organisms/self-checkin/identity-verify/identity-verify-upload-section'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { IdentityVerifyCamera } from '@/components/organisms/self-checkin/identity-verify/identity-verify-camera'
import { IdentityDocumentCaptureItem } from '@/components/organisms/self-checkin/identity-verify/identity-document-capture-item'
import { getIdentificationRequirement, IdentificationRequirement } from '@/libs/self-checkin'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { PaxSelect } from '@/components/molecules/pax-select'
import { countryListWithFlag } from '@/libs/nationality'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

type ContainerProps = {
  title: string
}

const PassportSampleImage: string = require('@/static/images/self-checkin/passport-sample.png')

export const UploadPassport: React.FC<ContainerProps> = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()
  const nationalities = countryListWithFlag(i18n.language)

  const [representativePassportImage, setRepresentativePassportImage] = useState<string>()
  const [representativePassportObjectKey, setRepresentativePassportObjectKey] = useState<string | undefined>()
  const [nationality, setNationality] = useState<string | undefined>()
  const [detectNationality, setDetectNationality] = useState<string | undefined>()
  const [companionImageArray, setCompanionImageArray] = useState<
    {
      id: number
      companionPassportImage: string
      objectKey: string
    }[]
  >([])
  const [activateCameraCompanionIndex, setActivateCameraCompanionIndex] = useState<number>()
  const [companionNumber, setCompanionNumber] = useState<number>(
    (state.accommodationInfo?.paxTotal || state.selectReservation?.paxTotal || 1) - 1,
  )
  const [activateCameraType, setActivateCameraType] = useState<'representativePassport' | 'companionPassport' | undefined>(undefined)
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)

  const uploadImage = (imageType: 'representativePassport' | 'companionPassport', index?: number): void => {
    setActivateCameraType(imageType)
    setActivateCameraCompanionIndex(index)
  }

  const onShutter = (image: any, nationality: string | undefined, objectKey: string) => {
    if (activateCameraType === 'representativePassport') {
      setRepresentativePassportImage(image)
      setRepresentativePassportObjectKey(objectKey)
      setNationality(nationality)
      setDetectNationality(nationality)
    } else if (activateCameraCompanionIndex !== undefined) {
      const photoInState = companionImageArray.findIndex(v => v.id === activateCameraCompanionIndex)
      if (photoInState !== -1) {
        companionImageArray[photoInState].companionPassportImage = image
        companionImageArray[photoInState].objectKey = objectKey
        setCompanionImageArray([...companionImageArray])
      } else {
        setCompanionImageArray([
          ...companionImageArray,
          {
            id: activateCameraCompanionIndex,
            companionPassportImage: image,
            objectKey,
          },
        ])
      }
    }
    setActivateCameraType(undefined)
  }

  const isDisabled = (): boolean => {
    if (representativePassportImage && companionNumber === 0) {
      return false
    }

    if (representativePassportImage && companionImageArray.length) {
      for (let index = 0; index < companionNumber; index++) {
        const image = companionImageArray.find(img => img.id === index)
        if (image) continue

        return true
      }

      return false
    }

    return true
  }

  const submit = () => {
    saveState({
      ...state,
      uploadLicense: {
        nationality,
        representativePassportImage,
        representativePassportObjectKey,
        companionImageArray,
      },
    })
    history.push({ pathname: '/self-checkin/confirm' })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'setting')) {
      return
    }

    if (getIdentificationRequirement(state, state.accommodationInfo?.nationality) === IdentificationRequirement.None) {
      history.replace({ pathname: '/self-checkin/confirm' })
      return
    }
    setIsInitializeComponent(true)
    if (state.uploadLicense?.representativePassportImage) {
      setNationality(state.uploadLicense.nationality)
      setRepresentativePassportImage(state.uploadLicense.representativePassportImage)
      setRepresentativePassportObjectKey(state.uploadLicense.representativePassportObjectKey)
    }
    if (state.uploadLicense?.companionImageArray) {
      setCompanionImageArray(state.uploadLicense.companionImageArray)
    }
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }
  return (
    <SelfCheckinLayout
      state={state}
      fullscreen={Boolean(activateCameraType)}
      header={!activateCameraType ? { goToPreviousPage: history.goBack, title: t('Identification') } : undefined}
      footer={!activateCameraType ? { right: { isDisabled: isDisabled(), label: t('Next'), onClick: submit } } : undefined}
    >
      {!activateCameraType && (
        <IdentityVerifyUploadSection
          type="passport"
          heading={t('Please take a photo of the passports of all guests')}
          notice={
            <Trans i18nKey="Guests without an address in Japan are required by law to present their passport. The facility will also keep a copy of it. Registered passport images will be used to manage the guest register and to verify the identity of each guest." />
          }
        >
          <div css={uploadWrapperStyle}>
            <div css={representativeSectionStyle}>
              <div>
                <h3 css={[sectionTitleStyle, representativeTitleStyle]}>{t('Representative')}</h3>
                <IdentityDocumentCaptureItem
                  type="passport"
                  image={representativePassportImage}
                  sampleImage={PassportSampleImage}
                  label={t('Take a Photo')}
                  onCapture={() => uploadImage('representativePassport')}
                />
              </div>
              {representativePassportImage && (
                <div css={nationalityFieldStyle}>
                  <label css={[sectionTitleStyle, nationalityLabelStyle]} htmlFor="passport-nationality">
                    {t('Country name')}
                  </label>
                  {detectNationality && <p css={detectedNationalityStyle}>{t('Detected from your passport')}</p>}
                  <select
                    id="passport-nationality"
                    css={nationalitySelectStyle}
                    value={nationality}
                    onChange={e => setNationality(e.target.value)}
                  >
                    <option value={''}>{t('Country name')}</option>
                    {nationalities.map(country => (
                      <option value={country.value} key={country.value}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div css={companionSectionStyle}>
              <div css={companionHeaderStyle}>
                <h3 css={sectionTitleStyle}>{t('Companion')}</h3>
                <PaxSelect isEnabledZero value={`${companionNumber}`} onClick={val => setCompanionNumber(Number(val))} />
              </div>
              {companionNumber > 0 && (
                <div css={companionListStyle}>
                  {new Array(companionNumber).fill(null).map((_, i) => (
                    <IdentityDocumentCaptureItem
                      key={i}
                      type="passport"
                      image={companionImageArray.find(img => img.id === i)?.companionPassportImage}
                      sampleImage={PassportSampleImage}
                      label={t('Take a Photo')}
                      onCapture={() => uploadImage('companionPassport', i)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </IdentityVerifyUploadSection>
      )}
      {activateCameraType && (
        <IdentityVerifyCamera onCancel={() => setActivateCameraType(undefined)} onShutter={onShutter} type="passport" />
      )}
    </SelfCheckinLayout>
  )
}

// 代表者の写真と同行者の写真を同じ幅にするため、縦線の分の1px も列として持つ
const uploadWrapperStyle = css(
  {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 1px repeat(2, minmax(0, 1fr))',
    columnGap: 32,
    marginTop: 40,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { display: 'block' },
  }),
)

const representativeSectionStyle = css(
  {
    minWidth: 0,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 32 },
  }),
)

const companionSectionStyle = css(
  {
    gridColumn: '2 / -1',
    minWidth: 0,
    borderLeft: '1px solid #CCCCCC',
    paddingLeft: 32,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { marginTop: 40, paddingTop: 40, paddingLeft: 0, borderLeft: 'none', borderTop: '1px solid #CCCCCC' },
  }),
)

const sectionTitleStyle = css(
  {
    margin: 0,
    color: '#272727',
    fontSize: 20,
    fontWeight: 'bold',
    lineHeight: '27px',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 24, lineHeight: '33px' },
  }),
)

// 横向きでは、隣の同行者の人数の選択欄と見出しの高さをそろえる
const representativeTitleStyle = css(
  {
    display: 'flex',
    alignItems: 'center',
    minHeight: 59,
    marginBottom: 16,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { minHeight: 0 },
    landscape13: { minHeight: 64 },
  }),
)

const companionHeaderStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 32,
})

const companionListStyle = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  columnGap: 32,
  rowGap: 40,
  marginTop: 16,
})

const nationalityFieldStyle = css(
  {
    marginTop: 40,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { marginTop: 0 },
  }),
)

const nationalityLabelStyle = css({
  display: 'block',
  marginBottom: 16,
})

const detectedNationalityStyle = css({
  margin: '0 0 16px',
  color: '#676767',
  fontSize: 16,
  lineHeight: '22px',
})

const nationalitySelectStyle = css(
  {
    display: 'block',
    width: '100%',
    height: 59,
    padding: '0 64px 0 32px',
    border: '1px solid #CCCCCC',
    borderRadius: 40,
    color: '#272727',
    fontSize: 22,
    fontWeight: 500,
    letterSpacing: '1.1px',
    appearance: 'none',
    backgroundColor: '#FFFFFF',
    backgroundImage: `url(${require('@/static/images/arrow_down_yellow.svg')})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 32px center',
    backgroundSize: 14,
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { height: 64, fontSize: 26, letterSpacing: '1.3px' },
  }),
)
